import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import "./CustomDriverStyles.css";
import "./WelcomeModal.css";

type WelcomeModalProps = {
  onStart: () => void;
  onSkip: () => void;
};

const WelcomeModal: React.FC<WelcomeModalProps> = ({ onStart, onSkip }) => {
  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleModalClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
  };

  return (
    <div className="welcome-overlay" onClick={handleOverlayClick}>
      <div className="welcome-modal" onClick={handleModalClick}>
        <img
          src="https://static.klipy.com/ii/e293a233a303a98e471f78d04e13a1b0/8c/4a/YmbPfp3m.webp"
          alt="Welcome"
          className="welcome-img"
        />
        <h2 className="welcome-title">
          Welcome to the KLIPY Partner Platform!
        </h2>
        <p>
          Explore how to get the most out of your dashboard. You
          <br></br>
          can follow this quick tour to learn key features, or
          <br></br>
          click "Skip" anytime to exit.
        </p>
        <div className="welcome-buttons">
          <button className="skip-btn" onClick={onSkip}>
            Skip
          </button>
          <button className="start-btn" onClick={onStart}>
            Begin!
          </button>
        </div>
      </div>
    </div>
  );
};

const steps = [
  {
    element: ".details-cards-revenue",
    popover: {
      title: "Ad Performance & Revenue",
      description: `Keep track of your monetization in real-time. These cards show
        </br> 
        key metrics like revenue, impressions, CPM, CTR, fill rate, and
        </br>
        more - helping you analyze and optimize your ad performance if
        </br>
        you've integrated KLIPY's Ads API.`,
      position: "right",
    },
  },
  {
    element:
      ".details-cards-impressions > .details-cards-title > .card-title > .card-title-icon",
    popover: {
      title: "Get Detailed Insights",
      description: `Hover over the info icons on each metric card to see what the
        <br>
        data represents and how it reflects your ad performance. It's a
        <br>
        quick way to better understand your dashboard at a glance.`,
      position: "left",
    },
  },
  {
    element: ".details-cards-fill-rate",
    popover: {
      title: "Compare Data Over Time",
      description: `
        <video width="250" autoplay muted playsinline preload="auto">
            <source src="/videos/Chart.mp4" type="video/mp4">
            Your browser does not support the video tag.
        </video>
        <br>
        Use the charts to compare your selected time range with<br>
        the previous period. It's a quick way to identify trends, spot<br>
        drops or spikes, and optimize your ad performance.`,
      position: "left",
    },
  },
  {
    element: ".app-layout-main > .table-container",
    popover: {
      title: "Content Engagement Insights",
      description: `Track how users interact with your content - see how often
        <br>
        it's shared, searched, or viewed, including activity from the
        <br>
        Trending Content tab.
        <br>
        <div className="notes-for-popover">
          <p>Note: <span>View data is only shown for apps with the "view" tracking feature integrated.</span></p>
        </div>
        `,
      position: "left",
    },
  },
  {
    element: ".app-filters_left > :nth-child(1)",
    popover: {
      title: "Filter performance by content type",
      description:
        "If your app supports multiple KLIPY formats (GIFs, Clips, Stickers, etc.), use this filter to break down usage and monetization by type.",
      position: "left",
    },
  },
  {
    element: ".app-filters_left > :nth-child(2)",
    popover: {
      title: "Analyze metrics per app",
      description: `If you've integrated KLIPY into multiple apps (e.g., iOS and Android), this filter helps you compare performance across each app individually.
        </br>
        <div className="notes-for-popover">
        <p>Note: <span>Each app needs its own unique API key to track data separately.</span></p>
        </div>
        `,
      position: "left",
    },
  },
  {
    element: ".app-filters_left > :nth-child(3)",
    popover: {
      title: "View region-specific performance",
      description: `Use this filter to break down metrics by country and understand where your content performs best.
        </br>
        <div className="notes-for-popover">
        <p>Note: <span>Countries will only appear if your app has recorded usage there. The list updates based on selected app, date, and content filters.</span></p>
        </div>
        `,
      position: "left",
    },
  },
  {
    element: ".app-filters_right",
    popover: {
      title: "Track trends over time",
      description: `Switch between day, week, or month views—or use a custom range - to analyze how your metrics evolve.
        </br>
        <div className="notes-for-popover">
        <p>Note: <span>Period comparison charts aren't available for custom date ranges.</span>
        </p>
        </div>
        `,
      position: "botttom",
    },
  },
  {
    element: ".app-analytics",
    popover: {
      title: "Manage your API access here",
      description: `This page lists your app's API keys with details like creation date, status, and usage.
        </br>
        <div className="notes-for-popover">
        <p>Note: <span>Keys start in testing mode by default. For production access, email support@klipy.com.</span></p>
        </div>
        `,
      position: "left",
    },
  },
  {
    element: ".manage-content",
    popover: {
      title: "Control what content appears in your app",
      description: `Use this section to block keywords and set content rating filters (like G, PG, PG-13, etc.), globally or by country.
        </br>
        <div className="notes-for-popover">
        <p>Note: <span>Learn more about how KLIPY filters work</span></p>
        </div>
        `,
      position: "left",
    },
  },
];

const createProgressBubbles = (currentStep: number, totalSteps: number): string => {
  let bubblesHTML = '<div class="driver-progress-bubbles">';
  
  for (let i = 0; i < totalSteps; i++) {
    const isActive = i === currentStep;
    const isCompleted = i < currentStep;
    
    bubblesHTML += `<div class="driver-progress-bubble ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}"></div>`;
  }
  
  bubblesHTML += '</div>';
  return bubblesHTML;
};

const Onboarding: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showModal, setShowModal] = useState(false);

  const HAS_SEEN_WELCOME_MODAL_KEY = "hasSeenWelcomeModal";
  const TOUR_PROGRESS_KEY = "tourProgress";
  const TOUR_CURRENT_PATH_KEY = "tourCurrentPath";
  const TOUR_COMPLETED = "tourCompleted";

  useEffect(() => {
    const hasSeenModal = localStorage.getItem(HAS_SEEN_WELCOME_MODAL_KEY);
    const tourProgress = localStorage.getItem(TOUR_PROGRESS_KEY);
    const tourCompleted = localStorage.getItem(TOUR_COMPLETED);

    if (!hasSeenModal && !tourCompleted) {
      setShowModal(true);
    } else if (tourProgress && !tourCompleted) {
      resumeTour();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const saveTourProgress = (stepIndex: number, currentPath: string) => {
    localStorage.setItem(TOUR_PROGRESS_KEY, stepIndex.toString());
    localStorage.setItem(TOUR_CURRENT_PATH_KEY, currentPath);
  };

  const clearTourProgress = () => {
    localStorage.removeItem(TOUR_PROGRESS_KEY);
    localStorage.removeItem(TOUR_CURRENT_PATH_KEY);
  };

  const markModalAsSeen = () => {
    localStorage.setItem(HAS_SEEN_WELCOME_MODAL_KEY, "true");
    setShowModal(false);
  };

  const markTourAsCompletedByDone = () => {
    localStorage.setItem(TOUR_COMPLETED, "true");
    clearTourProgress();
    markModalAsSeen();
  };

  const resumeTour = () => {
    const savedStep = localStorage.getItem(TOUR_PROGRESS_KEY);
    const savedPath = localStorage.getItem(TOUR_CURRENT_PATH_KEY);

    if (savedStep && savedPath) {
      const stepIndex = parseInt(savedStep, 10);

      if (window.location.pathname !== savedPath) {
        navigate(savedPath);
      }

      setTimeout(() => {
        startTourFromStep(stepIndex);
      }, 200);
    }
  };

  const updateProgressBubbles = (tour: any) => {
    const currentStep = tour.getState().activeIndex ?? 0;
    const totalSteps = steps.length;
    const progressElement = document.querySelector('.driver-popover-progress-text');
    if (progressElement) {
      progressElement.innerHTML = createProgressBubbles(currentStep, totalSteps);
    }
  };

  const startTourFromStep = (startIndex: number = 0) => {
    const tour = driver({
      showProgress: true,
      showButtons: ["next", "previous", "close"],
      allowClose: true,
      disableActiveInteraction: true,
      nextBtnText: "Next",
      prevBtnText: "Back",
      doneBtnText: "Done",
      steps,

      onCloseClick: () => {
        const currentStepIndex = tour.getState().activeIndex ?? 0;
        const currentPath = window.location.pathname;
        saveTourProgress(currentStepIndex, currentPath);
        markTourAsCompletedByDone();
        console.log(`Tour closed at step ${currentStepIndex}. Progress saved.`);
        tour.destroy();
      },

      onNextClick: (element, step, options) => {
        const currentStepIndex = options.state.activeIndex ?? 0;
        let nextPath = window.location.pathname;

        const isLastStep = currentStepIndex === steps.length - 1;

        if (currentStepIndex === 7) {
          nextPath = "/api-keys";
          navigate("/api-keys");
          saveTourProgress(currentStepIndex + 1, nextPath);
          setTimeout(() => {
            tour.moveNext();
            updateProgressBubbles(tour);
          }, 100);
        } else if (currentStepIndex === 8) {
          nextPath = "/manage-content";
          navigate("/manage-content");
          saveTourProgress(currentStepIndex + 1, nextPath);

          const waitForApiKeysData = () => {
            const checkElement = () => {
              if (element) {
                if (isLastStep) {
                  markTourAsCompletedByDone();
                  tour.destroy();
                } else {
                  tour.moveNext();
                  updateProgressBubbles(tour);
                }
              } else {
                setTimeout(checkElement, 300);
              }
            };
            setTimeout(checkElement, 300);
          };
          waitForApiKeysData();
        } else if (isLastStep) {
          saveTourProgress(currentStepIndex + 1, nextPath);
          markTourAsCompletedByDone();
          tour.destroy();
        } else {
          saveTourProgress(currentStepIndex + 1, nextPath);
          tour.moveNext();
          updateProgressBubbles(tour);
        }
      },

      onPrevClick: (element, step, options) => {
        const currentStepIndex = options.state.activeIndex ?? 0;
        let prevPath = window.location.pathname;

        if (currentStepIndex === 9) {
          prevPath = "/api-keys";
          navigate("/api-keys");
          saveTourProgress(currentStepIndex - 1, prevPath);
          setTimeout(() => {
            tour.movePrevious();
            updateProgressBubbles(tour);
          }, 100);
        } else if (currentStepIndex === 8) {
          prevPath = "/";
          navigate("/");
          saveTourProgress(currentStepIndex - 1, prevPath);
          setTimeout(() => {
            tour.movePrevious();
            updateProgressBubbles(tour);
          }, 100);
        } else {
          saveTourProgress(currentStepIndex - 1, prevPath);
          tour.movePrevious();
          updateProgressBubbles(tour);
        }
      },

      onDestroyStarted: (element, step, options) => {
        const currentStep = options.state.activeIndex ?? 0;
        const isLastStep = currentStep === steps.length - 1;

        if (isLastStep) {
          console.log(
            'Dismissal initiated on last step (assuming "Done" button click)'
          );
          markTourAsCompletedByDone();
        }
      },

      onHighlighted: (element, step, options) => {
        const currentStepIndex = options.state.activeIndex ?? 0;
        saveTourProgress(currentStepIndex, window.location.pathname);
        setTimeout(() => {
          updateProgressBubbles(tour);
        }, 50);
      },
    });

    tour.drive(startIndex);
    
    setTimeout(() => {
      updateProgressBubbles(tour);
    }, 100);
  };

  const startTour = () => {
    markModalAsSeen();
    setShowModal(false);
    localStorage.removeItem(TOUR_COMPLETED);
    if (window.location.pathname !== "/") {
      navigate("/");
    }

    setTimeout(() => {
      startTourFromStep(0);
    }, 100);
  };

  const handleSkip = () => {
    markModalAsSeen();
    clearTourProgress();
    localStorage.removeItem(TOUR_COMPLETED);
  };

  return (
    <div>
      {showModal && <WelcomeModal onStart={startTour} onSkip={handleSkip} />}
    </div>
  );
};

export default Onboarding;
